import React, { useState, useRef } from 'react';
import isValidUrl from '@/lib/utils/isValidUrl';
import Dialog from '@/components/Dialog';
import Prompt from '@/components/Prompt';
import { FormattedMessage } from 'react-intl';
import styled from '@emotion/styled';

const InvalidUrlWarning = styled('span')(({ theme }) => ({
  color: theme.palette.warning.main,
}));

type ProofLinkPromptProps = {
  open: boolean;
  onOk: (input: string) => void;
  onCancel: () => void;
};

const ProofLinkPrompt = ({ open, onOk, onCancel }: ProofLinkPromptProps) => {
  const [input, setInput] = useState('');
  const [showInvalidUrlWarning, setShowInvalidUrlWarning] = useState(false);
  const [showEmptyError, setShowEmptyError] = useState(false);

  const handleCancel = (): void => {
    setShowInvalidUrlWarning(false);
    setShowEmptyError(false);
    onCancel();
  };

  const inputRef = useRef<HTMLInputElement>(null);

  const handleOk = (): void => {
    if (inputRef.current === null) {
      return;
    }

    if (!input.trim()) {
      setShowEmptyError(true);
      inputRef.current.focus();

      return;
    }

    onOk(input.trim());

    setInput('');
    setShowInvalidUrlWarning(false);
    setShowEmptyError(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newInput = e.target.value;

    setInput(newInput);
    setShowEmptyError(false);

    const hasInput = !!newInput.trim();
    const urlIsValid = isValidUrl(newInput);
    setShowInvalidUrlWarning(hasInput && !urlIsValid);
  };

  return (
    <Dialog open={open} onClose={handleCancel} onEnterClose={handleOk}>
      <Dialog.Title>
        <FormattedMessage id="portfolio.level.attachments.proof.proof_link.add.dialog.title" />
      </Dialog.Title>
      <Dialog.Content>
        <Dialog.ContentText>
          <FormattedMessage id="portfolio.level.attachments.proof.proof_link.add.dialog.text" />
        </Dialog.ContentText>
      </Dialog.Content>
      <Prompt.TextField
        label={
          <FormattedMessage id="portfolio.level.attachments.proof.proof_link.add.dialog.input_label" />
        }
        value={input}
        onChange={handleInputChange}
        error={showEmptyError}
        helperText={
          (showInvalidUrlWarning && (
            <InvalidUrlWarning>
              <FormattedMessage id="portfolio.level.attachments.proof.proof_link.add.dialog.invalid_url_warning" />
            </InvalidUrlWarning>
          )) ||
          (showEmptyError && (
            <FormattedMessage id="portfolio.level.attachments.proof.proof_link.add.dialog.empty_error" />
          ))
        }
        inputRef={inputRef}
      />
      <Dialog.Actions addTopMargin>
        <Dialog.Actions.CancelButton onClick={handleCancel} />
        <Dialog.Actions.Button color="primary" onClick={handleOk}>
          <FormattedMessage id="portfolio.level.attachments.proof.proof_link.add.dialog.add_button" />
        </Dialog.Actions.Button>
      </Dialog.Actions>
    </Dialog>
  );
};

export default ProofLinkPrompt;
